import { drainPendingGames, clearPendingGames, quarantineGame, type PendingOfflineGame } from './offlineDb';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';
// Backend `OfflineSyncRequest.games` en fazla bu kadar öğe kabul ediyor
// (backend/main.py OFFLINE_SYNC_MAX_GAMES) — client aynı sınırla batch'ler.
const SYNC_BATCH_SIZE = 20;

let _syncing = false;

export interface SyncResult {
  synced: number;
  rejected: number;
}

interface SyncItemResult {
  client_id: string;
  status: 'accepted' | 'duplicate' | 'rejected';
  reason?: string;
}

function toPayload(g: PendingOfflineGame) {
  return {
    client_id: g.client_id ?? `${g.completed_at}-${g.id}`,
    puzzle: g.puzzle,
    solution: g.solution,
    difficulty: g.difficulty,
    duration_seconds: g.duration_seconds,
    mistakes: g.mistakes,
    hints_used: g.hints_used,
    completed_at: g.completed_at / 1000,
    won: g.won,
    ticket: g.ticket,
  };
}

export async function syncPendingGames(token: string | null, userId: string | null): Promise<SyncResult | null> {
  if (_syncing) return null;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return null;

  _syncing = true;
  try {
    let pending = await drainPendingGames();
    if (pending.length === 0) return { synced: 0, rejected: 0 };

    // Anonim başlanmış (owner_user_id=null) oyunlar hiçbir zaman sync edilemez —
    // bilet üretilirken sync_allowed=false olarak imzalanmış, sonradan (kullanıcı
    // giriş yapsa bile) hiçbir hesaba bağlanamaz. Kuyrukta sonsuza dek birikip
    // yerel depolamayı şişirmesinler diye (Codex bulgusu, doğrulandı) doğrudan
    // karantinaya taşınırlar — token/oturum durumundan bağımsız, her çağrıda.
    const unsyncable = pending.filter(g => g.owner_user_id === null && g.id !== undefined);
    if (unsyncable.length > 0) {
      for (const g of unsyncable) await quarantineGame(g, 'anonymous_unsyncable');
      await clearPendingGames(unsyncable.map(g => g.id!));
      pending = pending.filter(g => g.owner_user_id !== null);
    }

    if (!token) return { synced: 0, rejected: 0 };

    // Farklı bir hesapta oynanmış kayıtlar hiç gönderilmez; kuyrukta kalır,
    // gerçek sahibi giriş yapınca senkronize olur. Sunucu zaten bileti/owner'ı
    // doğruluyor, bu yalnızca gereksiz reddi önlüyor.
    const ownGames = pending.filter(g => g.owner_user_id === userId);
    if (ownGames.length === 0) return { synced: 0, rejected: 0 };

    let synced = 0;
    let rejected = 0;

    for (let i = 0; i < ownGames.length; i += SYNC_BATCH_SIZE) {
      const batch = ownGames.slice(i, i + SYNC_BATCH_SIZE);
      const res = await fetch(`${API}/game/sync-offline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ games: batch.map(toPayload) }),
      });
      if (!res.ok) continue; // bu batch başarısız — kuyrukta kalır, bir sonraki syncte tekrar denenir
      const data = await res.json();
      const results = (data.results || []) as SyncItemResult[];
      const byClientId = new Map(results.map(r => [r.client_id, r]));

      // Her öğe kendi sonucuna göre HEMEN temizlenir (accepted/duplicate → sil,
      // rejected → quarantineGame zaten kendi içinde atomik olarak taşıyor+siliyor).
      // Öncesinde tüm id'ler toplanıp sona bırakılıyordu — arada bir hata olursa
      // aynı kayıt bir sonraki sync'te tekrar karantinaya kopyalanabilirdi
      // (Codex bulgusu, doğrulandı).
      for (const g of batch) {
        const clientId = g.client_id ?? `${g.completed_at}-${g.id}`;
        const result = byClientId.get(clientId);
        if (!result || g.id === undefined) continue;
        if (result.status === 'accepted' || result.status === 'duplicate') {
          synced++;
          await clearPendingGames([g.id]);
        } else {
          rejected++;
          await quarantineGame(g, result.reason || 'unknown');
        }
      }
    }

    return { synced, rejected };
  } catch {
    return null;
  } finally {
    _syncing = false;
  }
}
